// EnhancedMarkdown.jsx - Version adaptée pour les actualités avec gestion des liens vers articles
import React from 'react';
import { ExternalLink } from 'lucide-react';

const EnhancedMarkdown = ({ 
  content, 
  className = '',
  synthesisArticles = [],
  onReadNews = null
}) => {
  if (!content || typeof content !== 'string') {
    return null;
  }

  // Fonction pour parser le contenu et retourner des éléments React
  const parseContent = (text) => {
    // Diviser le contenu en lignes
    const lines = content.split('\n');
    const elements = [];
    let elementKey = 0;

    lines.forEach((line, lineIndex) => {
      const trimmedLine = line.trim();
      
      // Ligne vide - IGNORER pour réduire l'espacement
      if (!trimmedLine) {
        return;
      }

      // Traiter les titres markdown (## Titre)
      const headingMatch = trimmedLine.match(/^(#{1,3})\s+(.+)$/);
      if (headingMatch) {
        const level = headingMatch[1].length;
        const text = headingMatch[2];
        const HeadingTag = `h${level}`;
        const headingClasses = {
          1: "text-2xl font-bold text-gray-900 mb-3 mt-6",
          2: "text-xl font-semibold text-gray-800 mb-2 mt-5", 
          3: "text-lg font-semibold text-gray-800 mb-2 mt-4"
        };
        
        elements.push(
          React.createElement(
            HeadingTag,
            { 
              key: `heading-${lineIndex}`,
              className: headingClasses[level]
            },
            text
          )
        );
        return;
      }

      // Détecter les titres de propositions en gras en DÉBUT de ligne
      const propositionMatch = trimmedLine.match(/^\*\*(\d+\.\s[^*]+)\*\*(.*)$/);
      if (propositionMatch) {
        const titleText = propositionMatch[1];
        const remainingText = propositionMatch[2].trim();
        
        // Créer le titre comme élément séparé
        elements.push(
          <div 
            key={`proposition-${lineIndex}`}
            className="font-semibold text-gray-900 text-base mt-4 mb-2 block"
          >
            {titleText}
          </div>
        );
        
        // Si il y a du texte après le titre sur la même ligne, l'ajouter comme paragraphe
        if (remainingText) {
          elements.push(
            <p key={`proposition-text-${lineIndex}`} className="mb-3 text-base text-gray-700 leading-relaxed">
              {processLineText(remainingText, elementKey)}
            </p>
          );
        }
        return;
      }

      // Paragraphe normal
      const processedLine = processLineText(trimmedLine, elementKey);
      elementKey += 1000;
      
      elements.push(
        <p key={`p-${lineIndex}`} className="mb-3 text-base text-gray-700 leading-relaxed">
          {processedLine}
        </p>
      );
    });

    return elements;
  };

  // Fonction pour traiter le texte d'une ligne (liens, gras, etc.)
  const processLineText = (text, startIndex = 0) => {
    if (!text) return [];
    
    const elements = [];
    let lastIndex = 0;

    // Traiter les liens markdown avec gestion spéciale pour les articles de news
    const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
      // Ajouter le texte avant le lien
      if (match.index > lastIndex) {
        const beforeText = text.slice(lastIndex, match.index);
        if (beforeText) {
          elements.push(processSimpleText(beforeText, startIndex + lastIndex));
        }
      }
      
      const linkText = match[1];
      const linkUrl = match[2];
      
      // Vérifier si c'est un lien vers un article de news
      const articleInfo = synthesisArticles?.find(article => article.url === linkUrl);
      
      if (articleInfo && onReadNews) {
        // Lien vers un article de news - 🔧 CORRECTION 3 : Priorité à l'ouverture directe
        elements.push(
          <button
            key={`news-link-${startIndex + match.index}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // 🔧 CORRECTION 3 : Forcer l'ouverture dans le navigateur si URL disponible
              if (articleInfo.url && articleInfo.url !== '#') {
                window.open(articleInfo.url, '_blank', 'noopener,noreferrer');
              } else if (onReadNews) {
                onReadNews(articleInfo);
              }
            }}
            className="inline-flex items-center gap-1 px-2 py-1 mx-1 bg-blue-50 text-blue-600 rounded-md text-base hover:bg-blue-100 transition-colors font-medium cursor-pointer border border-blue-200"
            title={`Ouvrir l'article: ${articleInfo.title} (${articleInfo.source})`}
            type="button"
          >
            <span>{linkText}</span>
            <ExternalLink size={12} />
          </button>
        );
      } else {
        // Lien externe standard
        elements.push(
          <a
            key={`external-link-${startIndex + match.index}`}
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2 py-1 mx-1 bg-green-50 text-green-600 rounded-md text-base hover:bg-green-100 transition-colors font-medium border border-green-200"
          >
            <span>{linkText}</span>
            <ExternalLink size={12} />
          </a>
        );
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    // Ajouter le texte restant
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      if (remainingText) {
        elements.push(processSimpleText(remainingText, startIndex + lastIndex));
      }
    }
    
    return elements.length > 0 ? elements : [processSimpleText(text, startIndex)];
  };

  // Fonction pour traiter le texte simple (gras normal, italique, URLs)
  const processSimpleText = (text, startIndex) => {
    const elements = [];
    let lastIndex = 0;

    // Traiter les URLs nues
    const urlRegex = /(https?:\/\/[^\s\)]+)/g;
    let urlMatch;
    
    while ((urlMatch = urlRegex.exec(text)) !== null) {
      // Texte avant l'URL
      if (urlMatch.index > lastIndex) {
        const beforeText = text.slice(lastIndex, urlMatch.index);
        elements.push(processBoldItalic(beforeText, startIndex + lastIndex));
      }
      
      // URL cliquable
      const url = urlMatch[1];
      const displayUrl = url.length > 40 ? `${url.substring(0, 40)}...` : url;
      elements.push(
        <a
          key={`url-${startIndex + urlMatch.index}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline text-base"
        >
          <span>{displayUrl}</span>
          <ExternalLink size={12} />
        </a>
      );
      
      lastIndex = urlMatch.index + urlMatch[0].length;
    }
    
    // Texte restant
    if (lastIndex < text.length) {
      elements.push(processBoldItalic(text.slice(lastIndex), startIndex + lastIndex));
    }
    
    return elements.length > 0 ? elements : [processBoldItalic(text, startIndex)];
  };

  // Fonction pour traiter gras et italique
  const processBoldItalic = (text, startIndex) => {
    if (!text) return null;
    
    const elements = [];
    let lastIndex = 0;

    // Traiter le gras **texte** (mais PAS les titres de propositions qui sont déjà gérés)
    const boldRegex = /\*\*(.*?)\*\*/g;
    let boldMatch;
    
    while ((boldMatch = boldRegex.exec(text)) !== null) {
      // Ignorer si c'est un titre de proposition (commence par chiffre+point)
      if (/^\d+\.\s/.test(boldMatch[1])) {
        continue;
      }

      // Texte avant le gras
      if (boldMatch.index > lastIndex) {
        const beforeText = text.slice(lastIndex, boldMatch.index);
        if (beforeText) {
          elements.push(
            <span key={`text-${startIndex + lastIndex}`}>
              {beforeText}
            </span>
          );
        }
      }
      
      // Texte en gras normal
      elements.push(
        <strong 
          key={`bold-${startIndex + boldMatch.index}`}
          className="font-semibold text-gray-900 bg-blue-50 px-1 rounded text-base"
        >
          {boldMatch[1]}
        </strong>
      );
      
      lastIndex = boldMatch.index + boldMatch[0].length;
    }
    
    // Texte restant
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      if (remainingText) {
        elements.push(
          <span key={`text-${startIndex + lastIndex}`}>
            {remainingText}
          </span>
        );
      }
    }
    
    return elements.length > 0 ? elements : text;
  };

  const parsedElements = parseContent(content);

  return (
    <div className={`max-w-none ${className}`}>
      
      {/* Contenu parsé */}
      <div className="prose prose-base max-w-none">
        <div className="text-gray-800 leading-relaxed space-y-3">
          {parsedElements}
        </div>
      </div>
    </div>
  );
};

export default EnhancedMarkdown;